import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ElMessage } from 'element-plus';
import NProgress from 'nprogress';
import { storage, STORAGE_KEYS } from './storage';
import type { ApiResult } from '@/types/api';

NProgress.configure({ showSpinner: false });

export const BUSINESS_CODE = {
  SUCCESS: 0,
  TOKEN_EXPIRED: 1001,
  CLIENT_TYPE_MISMATCH: 1003,
  USER_NOT_FOUND: 1004,
  DISABLED: 1005,
  NO_ADMIN: 1006,
} as const;

const SUCCESS_CODES = new Set([BUSINESS_CODE.SUCCESS, 201, 4001]);

const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  timeout: 30000,
});

let isLoggingOut = false;
function forceLogout(reason: string) {
  if (isLoggingOut) return;
  isLoggingOut = true;
  storage.clear();
  ElMessage.error(reason || '登录已过期');
  setTimeout(() => {
    location.href = '/#/login';
    setTimeout(() => location.reload(), 50);
  }, 400);
}

service.interceptors.request.use((config) => {
  NProgress.start();
  const token = storage.get<string>(STORAGE_KEYS.TOKEN);
  if (token) {
    config.headers.Authorization = `bearer ${token}`;
  }
  return config;
});

// 注：拦截器实际返回 body.data（unknown）作为业务层期望的"数据"，但 axios 类型严格要求
// onFulfilled 返回 AxiosResponse；以前"全是 any"的写法让这个不一致悄无声息。
// 整个仓库其它代码都把 service.request 视作直接返回 data，所以这里强行 as any 让类型对齐。
service.interceptors.response.use(
  ((response: any) => {
    NProgress.done();

    // 二进制下载（blob/arraybuffer）跳过 JSON 解析，直接把 data 透传给业务层
    const respType = (response.config as AxiosRequestConfig)?.responseType;
    if (respType === 'blob' || respType === 'arraybuffer') {
      return response.data;
    }

    const body = response.data as ApiResult;

    if (SUCCESS_CODES.has(body.code)) {
      return body.data;
    }

    if (body.code === BUSINESS_CODE.TOKEN_EXPIRED) {
      forceLogout(body.msg || '登录已过期');
      return Promise.reject(new Error(body.msg));
    }

    // 其他业务错误默认弹 toast，除非通过 config.silent = true 关掉
    const silent = (response.config as AxiosRequestConfig & { silent?: boolean }).silent;
    if (!silent) {
      ElMessage.error(body.msg || '操作失败');
    }
    return Promise.reject(body);
  }) as any,
  (error) => {
    NProgress.done();
    const silent = (error?.config as AxiosRequestConfig & { silent?: boolean } | undefined)?.silent;
    if (!silent) {
      const msg = error?.response?.data?.msg || error?.message || '网络异常';
      ElMessage.error(msg);
    }
    return Promise.reject(error);
  }
);

interface RequestOptions extends AxiosRequestConfig {
  silent?: boolean;
}

export function get<T = unknown>(url: string, params?: Record<string, unknown>, opts: RequestOptions = {}) {
  return service.request<T, T>({ url, method: 'GET', params, ...opts });
}
export function post<T = unknown>(url: string, data?: Record<string, unknown>, opts: RequestOptions = {}) {
  return service.request<T, T>({ url, method: 'POST', data, ...opts });
}

export default service;
